/**
 * User-facing copy for failed clinical-trials API requests.
 */

/** Empty-state message when the server has no record for the request. */
export const NO_DATA_FOUND_MESSAGE = "No data found";

/**
 * Whether a rejected value carries an HTTP status code (e.g. shared `ApiError`).
 *
 * @param reason - Rejected value from an API call
 * @returns Status when present on the error object
 */
function readHttpStatus(reason: unknown): number | undefined {
  if (typeof reason !== "object" || reason === null || !("status" in reason)) {
    return undefined;
  }
  const status = (reason as { status: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

/**
 * Whether an error represents a missing resource (HTTP 404).
 *
 * @param reason - Rejected value from an API call
 * @returns True when the response was not found
 */
export function isNotFoundApiError(reason: unknown): boolean {
  if (readHttpStatus(reason) === 404) {
    return true;
  }
  if (reason instanceof Error) {
    return /request failed with status 404/i.test(reason.message);
  }
  return false;
}

/**
 * Map an API failure to copy suitable for empty-state UI.
 *
 * @param reason - Rejected value from an API call
 * @param fallback - Message when the error is not a known not-found case
 * @returns Display string for the empty or error state
 */
export function toUserFacingApiErrorMessage(
  reason: unknown,
  fallback: string,
): string {
  if (isNotFoundApiError(reason)) {
    return NO_DATA_FOUND_MESSAGE;
  }
  if (reason instanceof Error) {
    return reason.message;
  }
  return fallback;
}
